import React, { useState } from 'react'

import CtaButton from 'components/CtaButton'
import Grid from 'components/Grid'
import InputSearch from 'components/InputSearch'
import Rating from 'components/Rating'
import Typography from 'components/Typography'
import { useEventContext } from 'context/EventContext'
import { useListingDetailContext } from 'context/ListingDetailContext'

import ExpandableFeaturesList from './components/ExpandableFeaturesList'

import classes from './AllFeaturesSearch.module.scss'

const MIN_SEARCH_LENGTH = 3

/** @type {import("./types").default} */
const AllFeaturesSearch = () => {
  const { listing } = useListingDetailContext()
  const { list } = listing.features
  const { sendEvent } = useEventContext()

  const [search, setSearch] = useState('')
  const [productFeatures, setProductFeatures] = useState(list)

  const hasKeyFeatures = !!listing.features?.enhanced_features.length

  const handleSearch = e => {
    const { value } = e.target
    setSearch(value)
    if (value.length >= MIN_SEARCH_LENGTH) {
      setProductFeatures(list.filter(item => item.name?.toLowerCase().includes(value.toLowerCase())))
    } else {
      setProductFeatures(list)
    }
  }

  const onClear = () => {
    setSearch('')
    setProductFeatures(list)
    sendEvent({
      engagementComponent: 'product_key-features',
      engagementDestination: 'erase-search-query',
      engagementDetails: 'search-bar-features',
      name: 'engagement_search_bar',
    })
  }

  const onViewAllFeatures = () => {
    setSearch('')
    setProductFeatures(list)
    sendEvent({
      engagementComponent: 'product_key-features',
      engagementDestination: 'display_all-results',
      engagementDetails: 'text-link_view-all',
      name: 'engagement_search_bar',
    })
  }

  const onFocus = () => {
    sendEvent({
      engagementComponent: 'product_key-features',
      engagementDestination: 'search_features',
      engagementDetails: 'search-bar-features',
      name: 'engagement_search_bar',
    })
  }

  const isEmptySearch = search && !productFeatures.length

  return (
    <Grid spacing={4} container>
      <Grid item sm={6} lg={7}>
        {hasKeyFeatures ? (
          <Typography component="h3" variant="heading-md" className={classes.title}>
            All {listing.name} features
          </Typography>
        ) : (
          <Typography component="h2" className={classes.mainTitle}>
            {listing.name}&apos;s features
          </Typography>
        )}
        {!!listing.num_reviews && (
          <div className={classes.featuresRatingContainer}>
            <Typography className={classes.featuresRating}>Features rating:</Typography>
            <Rating.Link isCompact average={listing?.reviews?.by_field?.rating_features} totals={listing.num_reviews} />
          </div>
        )}
      </Grid>

      <Grid item xs={12} sm={6} lg={5}>
        <InputSearch
          placeholder="Search for a feature"
          value={search}
          onClear={onClear}
          onFocus={onFocus}
          onChange={handleSearch}
          testId="InputSearch-body"
        />
      </Grid>
      <Grid item xs={12}>
        {isEmptySearch ? (
          <div className={classes.rootEmpty}>
            <p className={classes.noFeaturesText}>{`No features contain "${search}".`}</p>
            <CtaButton variant="text" onClick={onViewAllFeatures}>
              VIEW ALL FEATURES
            </CtaButton>
          </div>
        ) : (
          <ExpandableFeaturesList
            features={productFeatures}
            search={search.length >= MIN_SEARCH_LENGTH ? search : ''}
            onViewAllFeatures={onViewAllFeatures}
          />
        )}
      </Grid>
    </Grid>
  )
}

export default AllFeaturesSearch
